// Damage Claim Types for Frontend

export enum DamageType {
  PHYSICAL = 'PHYSICAL',
  MECHANICAL = 'MECHANICAL',
}

export enum PhysicalDamageSubcategory {
  COLLISION = 'COLLISION',
  THEFT_VANDALISM = 'THEFT_VANDALISM',
  WEATHER_NATURAL_DISASTER = 'WEATHER_NATURAL_DISASTER',
  SOMETHING_ELSE = 'SOMETHING_ELSE',
}

export enum MechanicalIssueType {
  CHECK_ENGINE_LIGHT = 'CHECK_ENGINE_LIGHT',
  BRAKES_ISSUE = 'BRAKES_ISSUE',
  CLUTCH_ISSUE = 'CLUTCH_ISSUE',
  BATTERY_ISSUE = 'BATTERY_ISSUE',
  BROKE_DOWN_NOT_DRIVEABLE = 'BROKE_DOWN_NOT_DRIVEABLE',
  OTHER = 'OTHER',
}

export enum DamagedPart {
  // Body Damage
  DRIVER_SIDE_BODY = 'DRIVER_SIDE_BODY',
  FRONT_BUMPER_OR_GRILLE = 'FRONT_BUMPER_OR_GRILLE',
  FRONT_HOOD = 'FRONT_HOOD',
  PASSENGER_SIDE_BODY = 'PASSENGER_SIDE_BODY',
  REAR_BUMPER = 'REAR_BUMPER',
  ROOF = 'ROOF',
  TRUNK = 'TRUNK',
  // Tire & Rim
  DRIVER_SIDE_FRONT_TIRE_OR_RIM = 'DRIVER_SIDE_FRONT_TIRE_OR_RIM',
  DRIVER_SIDE_REAR_TIRE_OR_RIM = 'DRIVER_SIDE_REAR_TIRE_OR_RIM',
  PASSENGER_SIDE_FRONT_TIRE_OR_RIM = 'PASSENGER_SIDE_FRONT_TIRE_OR_RIM',
  PASSENGER_SIDE_REAR_TIRE_OR_RIM = 'PASSENGER_SIDE_REAR_TIRE_OR_RIM',
  // Windows & Mirrors
  DRIVER_SIDE_WINDOWS_OR_MIRRORS = 'DRIVER_SIDE_WINDOWS_OR_MIRRORS',
  PASSENGER_SIDE_WINDOWS_OR_MIRRORS = 'PASSENGER_SIDE_WINDOWS_OR_MIRRORS',
  FRONT_WINDSHIELD = 'FRONT_WINDSHIELD',
  REAR_WINDSHIELD = 'REAR_WINDSHIELD',
  // Other
  INTERIOR_DAMAGE = 'INTERIOR_DAMAGE',
  MECHANICAL_DAMAGE = 'MECHANICAL_DAMAGE',
  HEADLIGHTS_OR_TAIL_LIGHTS = 'HEADLIGHTS_OR_TAIL_LIGHTS',
  OTHER = 'OTHER',
}

export enum DriverAtIncident {
  GUEST = 'GUEST',
  SOMEONE_ELSE = 'SOMEONE_ELSE',
  NOT_BEING_DRIVEN = 'NOT_BEING_DRIVEN',
  UNKNOWN = 'UNKNOWN',
}

export enum RepairEstimateRange {
  BELOW_500 = 'BELOW_500',
  RANGE_500_1000 = 'RANGE_500_1000',
  RANGE_1000_2000 = 'RANGE_1000_2000',
  OVER_2000 = 'OVER_2000',
  UNKNOWN = 'UNKNOWN',
}

export enum YesNoUnknown {
  YES = 'YES',
  NO = 'NO',
  UNKNOWN = 'UNKNOWN',
}

export interface IncidentLocation {
  country?: string;
  streetAddress?: string;
  city?: string;
  postalCode?: string;
}

export interface RepairShopInfo {
  shopName?: string;
  address?: string;
  contactName?: string;
  phoneNumber?: string;
}

/** One slot in the report modal: either a new file upload or an existing trip photo URL. */
export type DamageEvidenceSlot =
  | { kind: 'upload'; file: File; previewUrl: string }
  | { kind: 'trip'; url: string };

export interface DamageClaimFormData {
  // Basic
  bookingId: string;
  damageType?: DamageType;
  physicalDamageSubcategory?: PhysicalDamageSubcategory;

  // Photos (ordered slots — avoids mixing blob previews with trip URLs on submit)
  beforeEvidenceSlots: DamageEvidenceSlot[];
  afterEvidenceSlots: DamageEvidenceSlot[];

  // Physical Damage Assessment
  policeReportFiled?: YesNoUnknown;
  waterInsideCar?: YesNoUnknown;
  whoWasDriving?: DriverAtIncident;
  anyoneInjured?: YesNoUnknown;
  anotherPersonInvolved?: YesNoUnknown;
  isCarDriveable?: YesNoUnknown;
  didVehicleLoseFluids?: YesNoUnknown;
  didAirbagsDeploy?: YesNoUnknown;

  // Incident Details
  incidentOccurredAt?: Date;
  incidentTime?: string;
  incidentLocation?: IncidentLocation;

  // Damaged Parts
  damagedParts: DamagedPart[];
  damagedPartsOtherDescription?: string;

  // Mechanical Issues
  mechanicalIssues: MechanicalIssueType[];
  mechanicalIssueOtherDescription?: string;

  // Descriptions
  damageDescription?: string;
  additionalInfo?: string;

  // Repair Shop
  repairShopInfo?: RepairShopInfo;

  // Estimate
  repairEstimateRange?: RepairEstimateRange;
}

export type StepId =
  | 'instructions'
  | 'damageType'
  // Physical damage flow
  | 'subcategory'
  | 'photos'
  | 'policeReport'
  | 'waterInsideCar'
  | 'isCarDriveable'
  | 'didVehicleLoseFluids'
  | 'didAirbagsDeploy'
  | 'anyoneInjured'
  | 'whoWasDriving'
  | 'anotherPersonInvolved'
  | 'incidentDateTime'
  | 'incidentLocation'
  | 'damagedParts'
  | 'damageDescription'
  | 'repairShopInfo'
  | 'additionalInfo'
  | 'repairEstimate'
  // Mechanical damage flow
  | 'mechanicalIsCarDriveable'
  | 'mechanicalIssues'
  | 'mechanicalDamageDescription'
  | 'mechanicalRepairShopInfo'
  | 'mechanicalAdditionalInfo';

export interface StepConfig {
  id: StepId;
  title: string;
  subtitle?: string;
}

// Flow configurations based on damage type and subcategory
export const COLLISION_FLOW: StepId[] = [
  'instructions',
  'damageType',
  'subcategory',
  'photos',
  'policeReport',
  'waterInsideCar',
  'isCarDriveable',
  'didVehicleLoseFluids',
  'didAirbagsDeploy',
  'anyoneInjured',
  'whoWasDriving',
  'anotherPersonInvolved',
  'incidentDateTime',
  'incidentLocation',
  'damagedParts',
  'damageDescription',
  'repairShopInfo',
  'additionalInfo',
  'repairEstimate',
];

export const THEFT_WEATHER_FLOW: StepId[] = [
  'instructions',
  'damageType',
  'subcategory',
  'photos',
  'didAirbagsDeploy',
  'didVehicleLoseFluids',
  'whoWasDriving',
  'anotherPersonInvolved',
  'anyoneInjured',
  'policeReport',
  'waterInsideCar',
  'incidentDateTime',
  'incidentLocation',
  'damagedParts',
  'isCarDriveable',
  'damageDescription',
  'repairShopInfo',
  'additionalInfo',
  'repairEstimate',
];

export const SOMETHING_ELSE_FLOW: StepId[] = [
  'instructions',
  'damageType',
  'subcategory',
  'photos',
  'incidentDateTime',
  'incidentLocation',
  'damagedParts',
  'isCarDriveable',
  'damageDescription',
  'repairShopInfo',
  'additionalInfo',
  'repairEstimate',
];

export const MECHANICAL_FLOW: StepId[] = [
  'instructions',
  'damageType',
  'mechanicalIsCarDriveable',
  'mechanicalIssues',
  'mechanicalDamageDescription',
  'mechanicalRepairShopInfo',
  'mechanicalAdditionalInfo',
];

export interface DamageClaimModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookingId: string;
  guestName?: string;
  onClaimSubmitted?: () => void;
  /** Trip photos from the loaded booking (host/guest query). Used for optional picker on report. */
  tripStartImages?: Array<{ url?: string | null } | null> | null;
  tripEndImages?: Array<{ url?: string | null } | null> | null;
}

export interface StepProps {
  formData: DamageClaimFormData;
  updateFormData: (updates: Partial<DamageClaimFormData>) => void;
  onNext: () => void;
  onBack: () => void;
  isSubmitting?: boolean;
  guestName?: string;
  /** Resolved trip photo URLs for the booking trip picker (report flow). */
  tripPickerItems?: { key: string; resolved: string; label: string }[];
  tripPhotoAllowlist?: Set<string>;
}

// Damage part labels for display
export const DAMAGED_PART_LABELS: Record<DamagedPart, string> = {
  [DamagedPart.DRIVER_SIDE_BODY]: 'Driver-side body',
  [DamagedPart.FRONT_BUMPER_OR_GRILLE]: 'Front bumper or grille',
  [DamagedPart.FRONT_HOOD]: 'Front hood',
  [DamagedPart.PASSENGER_SIDE_BODY]: 'Passenger-side body',
  [DamagedPart.REAR_BUMPER]: 'Rear bumper',
  [DamagedPart.ROOF]: 'Roof',
  [DamagedPart.TRUNK]: 'Trunk',
  [DamagedPart.DRIVER_SIDE_FRONT_TIRE_OR_RIM]: 'Driver-side front tire or rim',
  [DamagedPart.DRIVER_SIDE_REAR_TIRE_OR_RIM]: 'Driver-side rear tire or rim',
  [DamagedPart.PASSENGER_SIDE_FRONT_TIRE_OR_RIM]: 'Passenger-side front tire or rim',
  [DamagedPart.PASSENGER_SIDE_REAR_TIRE_OR_RIM]: 'Passenger-side rear tire or rim',
  [DamagedPart.DRIVER_SIDE_WINDOWS_OR_MIRRORS]: 'Driver-side windows or mirrors',
  [DamagedPart.PASSENGER_SIDE_WINDOWS_OR_MIRRORS]: 'Passenger-side windows or mirrors',
  [DamagedPart.FRONT_WINDSHIELD]: 'Front windshield',
  [DamagedPart.REAR_WINDSHIELD]: 'Rear windshield',
  [DamagedPart.INTERIOR_DAMAGE]: 'Interior damage',
  [DamagedPart.MECHANICAL_DAMAGE]: 'Mechanical damage',
  [DamagedPart.HEADLIGHTS_OR_TAIL_LIGHTS]: 'Headlights or tail lights',
  [DamagedPart.OTHER]: 'Other',
};

// Mechanical issue labels for display
export const MECHANICAL_ISSUE_LABELS: Record<MechanicalIssueType, string> = {
  [MechanicalIssueType.CHECK_ENGINE_LIGHT]: 'Check engine light on',
  [MechanicalIssueType.BRAKES_ISSUE]: 'Issue with the brakes',
  [MechanicalIssueType.CLUTCH_ISSUE]: 'Issue with the clutch',
  [MechanicalIssueType.BATTERY_ISSUE]: 'Issue with the battery',
  [MechanicalIssueType.BROKE_DOWN_NOT_DRIVEABLE]: 'Car broke down or is otherwise not driveable',
  [MechanicalIssueType.OTHER]: 'Other',
};

// Repair estimate labels for display
export const REPAIR_ESTIMATE_LABELS: Record<RepairEstimateRange, string> = {
  [RepairEstimateRange.BELOW_500]: 'Below $500',
  [RepairEstimateRange.RANGE_500_1000]: 'Between $500 and $1,000',
  [RepairEstimateRange.RANGE_1000_2000]: 'Between $1,000 and $2,000',
  [RepairEstimateRange.OVER_2000]: 'Over $2,000',
  [RepairEstimateRange.UNKNOWN]: "I don't know",
};
